/**
 * Flags Management Hook
 *
 * This custom hook provides functionality for fetching and managing quality control flags
 * for weather station data. It uses React Query for data fetching, caching, and pagination.
 *
 * Features:
 * - Data formatting with moment.js
 * - Infinite scrolling support
 * - Automatic caching
 * - Manual refresh capability
 * - Pagination handling
 */

import { useInfiniteQuery, useQueryClient } from "react-query";
import { getFormData } from "./api";
import moment from "moment";

/**
 * Formats a flag object with proper date formatting
 * @param {Object} flag - The raw flag data
 * @returns {Object} Formatted flag data with proper date strings
 */
const formatFlag = (flag) => ({
  id: flag.id,
  variable: flag.variable,
  from_date: flag.from_date
    ? moment(flag.from_date).format("MM/DD/YYYY, h:mm:ss a")
    : null,
  to_date: flag.to_date
    ? moment(flag.to_date).format("MM/DD/YYYY, h:mm:ss a")
    : null,
  QC_value: flag.QC_value,
  comment: flag.comment,
  aws_mapping_id: flag.aws_mapping_id,
  uid: flag.uid,
});

/**
 * Fetches flags with pagination
 * @param {Object} params - Query parameters
 * @param {number} [params.pageParam=0] - The page number to fetch
 * @returns {Promise<Object>} The response containing formatted flags and pagination info
 */
const fetchFlags = async ({ pageParam = 0 }) => {
  const response = await getFormData("flags", { page: pageParam, limit: 5 });
  return {
    flags: response.flags.map(formatFlag),
    nextPage: response.has_more ? pageParam + 1 : undefined,
    totalCount: response.total_count,
  };
};

/**
 * Custom hook for managing quality control flags
 * @returns {Object} Query results and utility functions
 * @property {Object} data - The query data
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 * @property {Function} refetchRows - Function to manually refresh the data
 */
export const useFlags = () => {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery("flags", fetchFlags, {
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // Cache kept for 30 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component mounts
  });

  /**
   * Manually refreshes the flags data
   * @returns {Promise} A promise that resolves when the data is refreshed
   */
  const refetchRows = async () => {
    await queryClient.resetQueries("flags");
    return query.refetch();
  };

  return { ...query, refetchRows };
};
